/**
 * @fileoverview Modal for resolving edit conflicts.
 *
 * Displays when two users try to edit the same resource and shows
 * a simple choice between keeping your changes or accepting theirs.
 */

import React from 'react';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './ConflictModal.module.css';

/**
 * ConflictModal component.
 *
 * @param {Object} props - Component props
 * @param {Object} props.conflict - Conflict data from useConflictResolution
 * @param {Function} props.onKeepMine - Handler for keeping user's changes
 * @param {Function} props.onAcceptTheirs - Handler for accepting server changes
 * @param {Function} props.onCancel - Handler for canceling (dismissing)
 * @param {boolean} props.isResolving - Whether resolution is in progress
 * @param {string} [props.resourceType='item'] - Type of resource for display
 */
export function ConflictModal({
    conflict,
    onKeepMine,
    onAcceptTheirs,
    onCancel,
    isResolving,
    resourceType = 'item',
}) {
    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(!!conflict);

    if (!conflict) return null;

    // Format a value for display
    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') {
            // Format as currency if it looks like money
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(value);
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (Array.isArray(value)) return `${value.length} items`;
        return String(value);
    };

    // Get the key fields to show in comparison
    const getComparisonFields = () => {
        const yourData = conflict.yourData || {};
        const serverData = conflict.serverData || {};

        // Find fields that are different
        const allKeys = new Set([...Object.keys(yourData), ...Object.keys(serverData)]);
        const differentFields = [];

        // Priority fields to always show if they exist
        const priorityFields = ['amount', 'description', 'date', 'type', 'name'];

        for (const key of allKeys) {
            // Skip internal fields
            if (['id', 'version', 'createdAt', 'createdBy', 'budgetKey', 'budgetMonth'].includes(key)) {
                continue;
            }

            const yourValue = yourData[key];
            const serverValue = serverData[key];

            // Check if values are different (simple comparison)
            if (JSON.stringify(yourValue) !== JSON.stringify(serverValue)) {
                differentFields.push({
                    key,
                    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                    yourValue,
                    serverValue,
                    priority: priorityFields.includes(key) ? 0 : 1,
                });
            }
        }

        // Sort by priority, then by key
        return differentFields.sort((a, b) => a.priority - b.priority || a.key.localeCompare(b.key));
    };

    const differences = getComparisonFields();

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} role="dialog" aria-modal="true">
                <div className={styles.header}>
                    <span className={styles.icon}>&#9888;</span>
                    <h2 className={styles.title}>Edit Conflict</h2>
                </div>

                <p className={styles.message}>
                    This {resourceType} was modified by{' '}
                    <strong>{conflict.modifiedBy || 'another user'}</strong>{' '}
                    while you were editing.
                </p>

                {differences.length > 0 && (
                    <div className={styles.comparison}>
                        <div className={styles.comparisonHeader}>
                            <span>Field</span>
                            <span>Your Changes</span>
                            <span>Current Value</span>
                        </div>
                        {differences.slice(0, 5).map(field => (
                            <div key={field.key} className={styles.comparisonRow}>
                                <span className={styles.fieldLabel}>{field.label}</span>
                                <span className={styles.yourValue}>
                                    {formatValue(field.yourValue)}
                                </span>
                                <span className={styles.serverValue}>
                                    {formatValue(field.serverValue)}
                                </span>
                            </div>
                        ))}
                        {differences.length > 5 && (
                            <p className={styles.moreChanges}>
                                +{differences.length - 5} more changes
                            </p>
                        )}
                    </div>
                )}

                <div className={styles.actions}>
                    <button
                        onClick={onKeepMine}
                        disabled={isResolving}
                        className={styles.primaryButton}
                    >
                        {isResolving ? 'Saving...' : 'Keep My Changes'}
                    </button>
                    <button
                        onClick={onAcceptTheirs}
                        disabled={isResolving}
                        className={styles.secondaryButton}
                    >
                        Accept Their Changes
                    </button>
                    <button
                        onClick={onCancel}
                        disabled={isResolving}
                        className={styles.cancelButton}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConflictModal;
